import React, { createContext, useContext, useState, useEffect } from 'react';

type SidebarBehavior = 'auto' | 'open' | 'closed';

interface SidebarBehaviorContextProps {
  sidebarBehavior: SidebarBehavior;
  setSidebarBehavior: (behavior: SidebarBehavior) => void;
  cycleBehavior: () => void;
}

const SidebarBehaviorContext = createContext<SidebarBehaviorContextProps | undefined>(undefined);

export const useSidebarBehavior = () => {
  const context = useContext(SidebarBehaviorContext);
  if (!context) {
    throw new Error('useSidebarBehavior must be used within a SidebarBehaviorProvider');
  }
  return context;
};

interface SidebarBehaviorProviderProps {
  children: React.ReactNode;
}

export const SidebarBehaviorProvider: React.FC<SidebarBehaviorProviderProps> = ({ children }) => {
  const [sidebarBehavior, setSidebarBehaviorState] = useState<SidebarBehavior>('auto');

  // Carregar estado do localStorage na inicialização
  useEffect(() => {
    const saved = localStorage.getItem('sidebarBehavior');
    if (saved && ['auto', 'open', 'closed'].includes(saved)) {
      setSidebarBehaviorState(saved as SidebarBehavior);
    }
  }, []);

  // Salvar no localStorage quando o estado mudar
  const setSidebarBehavior = (behavior: SidebarBehavior) => {
    setSidebarBehaviorState(behavior);
    localStorage.setItem('sidebarBehavior', behavior);
  };

  // Ciclar entre os 3 estados: auto -> open -> closed -> auto
  const cycleBehavior = () => {
    const behaviors: SidebarBehavior[] = ['auto', 'open', 'closed'];
    const currentIndex = behaviors.indexOf(sidebarBehavior);
    const nextIndex = (currentIndex + 1) % behaviors.length;
    setSidebarBehavior(behaviors[nextIndex]);
  };

  return (
    <SidebarBehaviorContext.Provider
      value={{
        sidebarBehavior,
        setSidebarBehavior,
        cycleBehavior,
      }}
    >
      {children}
    </SidebarBehaviorContext.Provider>
  );
};
